"use client";

import Link from "next/link";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  resolveProUpgradeLimitContent,
  type ProUpgradeLimitVariant,
} from "@/lib/plan-limit-ui";

type Props = {
  open: boolean;
  variant: ProUpgradeLimitVariant | null;
  isPro?: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ProUpgradeLimitDialog({
  open,
  variant,
  isPro = false,
  onOpenChange,
}: Props) {
  if (!variant) return null;

  const { title, description, icon: Icon, iconClassName, showUpgradeCta } =
    resolveProUpgradeLimitContent(variant, isPro);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="gap-0 overflow-hidden border-border/50 bg-background p-0 shadow-[0_32px_80px_-28px_rgba(0,0,0,0.78)] sm:max-w-[440px]">
        <div className="border-b border-border/40 bg-gradient-to-br from-muted/40 via-background to-background px-8 pb-7 pt-8">
          <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-border/50 bg-background/90 shadow-sm">
            <Icon
              className={`size-5 text-foreground/70 ${iconClassName ?? ""}`}
              strokeWidth={1.75}
              aria-hidden
            />
          </div>
          <AlertDialogHeader className="space-y-3 text-left">
            <AlertDialogTitle className="font-sans text-[20px] font-[700] leading-snug tracking-[-0.03em] text-foreground">
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription className="font-sans text-[14px] leading-[1.65] text-muted-foreground">
              {description}
            </AlertDialogDescription>
          </AlertDialogHeader>
        </div>

        <AlertDialogFooter className="gap-3 px-8 pb-8 pt-5 sm:justify-end">
          {showUpgradeCta ? (
            <>
              <AlertDialogCancel className="mt-0 h-11 rounded-xl border-border/70 px-5 text-[13px] font-medium">
                Not now
              </AlertDialogCancel>
              <Button
                type="button"
                asChild
                className="h-11 rounded-xl bg-indigo-600 px-6 text-[13px] font-medium hover:bg-indigo-500"
              >
                <Link href="/billing/pricing">View Pro plans</Link>
              </Button>
            </>
          ) : (
            <AlertDialogCancel className="mt-0 h-11 rounded-xl border-border/70 px-5 text-[13px] font-medium">
              Got it
            </AlertDialogCancel>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
